/**
 * Static navigation items for client-side search.
 * These items are searched instantly without API calls.
 */

import { UserRole } from "@/api/generated.schemas";

export interface NavigationItem {
  type: "navigation";
  id: string;
  title: string;
  path: string;
  icon: string;
  keywords: string[];
  requiredRole?: UserRole;
}

/**
 * All searchable navigation items in the application.
 * Items with requiredRole are only visible to users with that role or higher.
 */
export const navigationItems: NavigationItem[] = [
  // Main navigation
  {
    type: "navigation",
    id: "nav-dashboard",
    title: "Dashboard",
    path: "/",
    icon: "Dashboard",
    keywords: ["home", "overview", "main", "start"],
  },
  {
    type: "navigation",
    id: "nav-learning",
    title: "Learning Journey",
    path: "/training",
    icon: "School",
    keywords: ["training", "courses", "learn", "education", "enroll", "thinkific"],
  },
  {
    type: "navigation",
    id: "nav-goals",
    title: "Goals",
    path: "/goals",
    icon: "TrackChanges",
    keywords: ["objectives", "milestones", "tasks", "targets", "progress", "achievements"],
  },
  {
    type: "navigation",
    id: "nav-coach",
    title: "AI Coach",
    path: "/chat",
    icon: "SmartToy",
    keywords: ["ai", "assistant", "chat", "help", "questions", "conversation", "advice"],
  },
  {
    type: "navigation",
    id: "nav-calls",
    title: "Calls",
    path: "/transcripts",
    icon: "Phone",
    keywords: ["transcripts", "recordings", "meetings", "fireflies", "conversations"],
  },
  {
    type: "navigation",
    id: "nav-community",
    title: "Community",
    path: "/community",
    icon: "Groups",
    keywords: ["social", "team", "colleagues", "posts", "events", "calendar"],
  },
  {
    type: "navigation",
    id: "nav-resources",
    title: "Resources",
    path: "/resources",
    icon: "Folder",
    keywords: ["files", "documents", "downloads", "library", "materials"],
  },

  // Manager/Director items
  {
    type: "navigation",
    id: "nav-reports",
    title: "Reports",
    path: "/reports",
    icon: "Assessment",
    keywords: ["analytics", "metrics", "team", "performance", "data", "insights"],
    requiredRole: UserRole.manager,
  },

  // User settings/profile
  {
    type: "navigation",
    id: "nav-profile",
    title: "Profile",
    path: "/profile",
    icon: "Person",
    keywords: ["settings", "account", "preferences", "avatar", "user"],
  },
  {
    type: "navigation",
    id: "nav-integrations",
    title: "Integrations",
    path: "/integrations",
    icon: "Extension",
    keywords: ["connect", "fireflies", "thinkific", "sync", "api"],
  },

  // Admin items
  {
    type: "navigation",
    id: "nav-admin",
    title: "Admin Settings",
    path: "/admin",
    icon: "AdminPanelSettings",
    keywords: ["administration", "manage", "users", "system", "configuration"],
    requiredRole: UserRole.administrator,
  },

  // Footer items
  {
    type: "navigation",
    id: "nav-about",
    title: "About",
    path: "/about",
    icon: "Info",
    keywords: ["version", "info", "privacy", "terms", "support", "help"],
  },
];

/**
 * Role hierarchy for permission checking.
 * Higher index = higher permission level.
 */
const roleHierarchy: UserRole[] = [
  UserRole.user,
  UserRole.manager,
  UserRole.director,
  UserRole.administrator,
];

/**
 * Check if a user role has access to a required role.
 */
function hasRoleAccess(userRole: UserRole, requiredRole: UserRole): boolean {
  const userIndex = roleHierarchy.indexOf(userRole);
  const requiredIndex = roleHierarchy.indexOf(requiredRole);
  return userIndex >= requiredIndex;
}

/**
 * Search navigation items by query.
 * Filters by role permissions and matches against title and keywords.
 *
 * @param query - Search query string
 * @param userRole - Current user's role
 * @returns Matching navigation items
 */
export function searchNavigation(query: string, userRole: UserRole): NavigationItem[] {
  if (!query || query.length < 2) {
    return [];
  }

  const queryLower = query.toLowerCase();

  return navigationItems.filter((item) => {
    // Check role permission
    if (item.requiredRole && !hasRoleAccess(userRole, item.requiredRole)) {
      return false;
    }

    // Match against title
    if (item.title.toLowerCase().includes(queryLower)) {
      return true;
    }

    // Match against keywords
    return item.keywords.some((keyword) => keyword.includes(queryLower));
  });
}
